/**
 * Edit User Modal
 *
 * Modal component for editing existing user information.
 * Pre-populates form with current user data and supports partial updates.
 *
 * Features:
 * - Pre-populated form fields
 * - Partial update support (only changed fields)
 * - Full form validation
 * - Role and department modification
 * - Locale and timezone updates
 * - React Query mutation with optimistic updates
 *
 * @component
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import {
  useUpdateUser,
  getDepartments,
  type User,
  type UpdateUserPayload
} from '../../../services/queries/userQueries';
import { useRoles } from '../../../services/queries/roleQueries';
import { useToast } from '../../../contexts/ToastContext';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea } from '../../../components/standard';
import { gobodoUserProvider } from '../../../services/providers/gobodoUserProvider';
import { RolePermissionPreview } from './RolePermissionPreview';

const USE_GOBODO_API = process.env.REACT_APP_USE_GOBODO_API === 'true';

// ============================================
// INTERFACES
// ============================================

interface EditUserModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  roleIds: string[]; // Changed to array for multi-select
  locale: string;
  timezone: string;
  emailSignature: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  roleIds?: string;
}

// ============================================
// MAIN COMPONENT
// ============================================

export const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose }) => {
  const updateUser = useUpdateUser();
  const { success, error } = useToast();
  const { logChange } = useSetupChanges();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    roleIds: [],
    locale: '',
    timezone: '',
    emailSignature: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // Get options
  const departments = getDepartments();
  // Fetch roles using React Query hook (supports both Gobodo API and mock data)
  const { data: roles = [], isLoading: rolesLoading } = useRoles({ includeSystem: true });
  const selectedRoles = useMemo(
    () => roles.filter((role) => formData.roleIds.includes(role.id)),
    [roles, formData.roleIds]
  );

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      // Get role IDs from user.roleIds or user.roles array
      const roleIds = user.roleIds || (user.roles ? user.roles.map(r => r.id) : []) || (user.roleId ? [user.roleId] : []);
      
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone || '',
        title: user.title || '',
        department: user.department || '',
        roleIds: roleIds,
        locale: user.locale || 'en_US',
        timezone: user.timezone || 'America/New_York',
        emailSignature: user.emailSignature || ''
      });
    }
  }, [user]);

  // Fetch roles from backend when modal opens (if using Gobodo API)
  useEffect(() => {
    if (user && isOpen && USE_GOBODO_API) {
      setIsLoadingRoles(true);
      gobodoUserProvider.getUserRoles(user.id)
        .then((fetchedRoles) => {
          if (fetchedRoles && fetchedRoles.length > 0) {
            const fetchedRoleIds = fetchedRoles.map(r => r.id);
            setFormData(prev => ({
              ...prev,
              roleIds: fetchedRoleIds
            }));
          }
        })
        .catch((error) => {
          console.error('Failed to fetch user roles from backend:', error);
          // Don't update formData - keep existing roles from user object
        })
        .finally(() => {
          setIsLoadingRoles(false);
        });
    }
  }, [user, isOpen]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    // Note: roleId validation removed - role assignment excluded from this phase

    // Phone validation (optional, but validate if provided)
    if (formData.phone && !/^\+?[\d\s()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleChange = (field: keyof FormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRoleToggle = (roleId: string) => {
    const currentRoleIds = formData.roleIds || [];
    const newRoleIds = currentRoleIds.includes(roleId)
      ? currentRoleIds.filter(id => id !== roleId)
      : [...currentRoleIds, roleId];
    handleChange('roleIds', newRoleIds);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[EditUserModal] handleSubmit called');

    if (!user || !validateForm()) {
      console.log('[EditUserModal] Validation failed or no user');
      return;
    }

    setIsSubmitting(true);
    console.log('[EditUserModal] Starting user update...');

    // Prepare payload - only include changed fields
    const updates: Partial<User> = {};

    if (formData.firstName.trim() !== user.firstName) {
      updates.firstName = formData.firstName.trim();
    }
    if (formData.lastName.trim() !== user.lastName) {
      updates.lastName = formData.lastName.trim();
    }
    if (formData.email.trim().toLowerCase() !== user.email) {
      updates.email = formData.email.trim().toLowerCase();
    }
    if (formData.phone.trim() !== (user.phone || '')) {
      updates.phone = formData.phone.trim() || undefined;
    }
    if (formData.title.trim() !== (user.title || '')) {
      updates.title = formData.title.trim() || undefined;
    }
    if (formData.department !== (user.department || '')) {
      updates.department = formData.department || undefined;
    }
    // Role updates are handled separately via assignRolesToUser/removeRolesFromUser
    if (formData.locale !== (user.locale || 'en_US')) {
      updates.locale = formData.locale;
    }
    if (formData.timezone !== (user.timezone || 'America/New_York')) {
      updates.timezone = formData.timezone;
    }
    if (formData.emailSignature !== (user.emailSignature || '')) {
      updates.emailSignature = formData.emailSignature;
    }

    // Calculate role changes
    // Get current user roles from user object
    const currentRoleIds = user.roleIds || (user.roles ? user.roles.map(r => r.id) : []) || (user.roleId ? [user.roleId] : []);
    const newRoleIds = formData.roleIds || [];
    const rolesToAdd = newRoleIds.filter(id => !currentRoleIds.includes(id));
    const rolesToRemove = currentRoleIds.filter(id => !newRoleIds.includes(id));

    console.log('[EditUserModal] Role changes:', {
      currentRoleIds,
      newRoleIds,
      rolesToAdd,
      rolesToRemove,
      USE_GOBODO_API
    });

    const payload: UpdateUserPayload = {
      userId: user.id,
      updates
    };

    updateUser.mutate(payload, {
      onSuccess: async () => {
        console.log('[EditUserModal] User update succeeded, checking role assignment...', {
          USE_GOBODO_API,
          rolesToAddLength: rolesToAdd.length,
          rolesToRemoveLength: rolesToRemove.length
        });

        // Handle role assignment/removal if using Gobodo API
        if (USE_GOBODO_API && (rolesToAdd.length > 0 || rolesToRemove.length > 0)) {
          console.log('[EditUserModal] Starting role assignment/removal...');
          try {
            if (rolesToAdd.length > 0) {
              console.log('[EditUserModal] Assigning roles:', rolesToAdd);
              await gobodoUserProvider.assignRolesToUser(user.id, rolesToAdd);
              console.log('[EditUserModal] Successfully assigned roles');
            }
            if (rolesToRemove.length > 0) {
              console.log('[EditUserModal] Removing roles:', rolesToRemove);
              await gobodoUserProvider.removeRolesFromUser(user.id, rolesToRemove);
              console.log('[EditUserModal] Successfully removed roles');
            }
          } catch (err: any) {
            // Handle error - show warning but don't fail entire update
            console.error('[EditUserModal] Failed to update user roles:', err);
            error('User updated, but role assignment failed', err.message || 'Please try updating roles again');
            setIsSubmitting(false);
            return; // Don't close modal, allow retry
          }
        } else {
          console.log('[EditUserModal] Skipping role assignment:', {
            USE_GOBODO_API,
            hasRolesToAdd: rolesToAdd.length > 0,
            hasRolesToRemove: rolesToRemove.length > 0
          });
        }

        const userName = `${formData.firstName} ${formData.lastName}`;

        // Track user update
        logChange({
          action: 'updated',
          entityType: 'user',
          entityName: userName,
          description: `updated user ${userName}`,
          performedBy: 'Admin'
        });

        success('User updated successfully!');
        onClose();
      },
      onError: (err: any) => {
        console.error('Failed to update user:', err);
        error('Failed to update user', err.message || 'Please try again');
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Edit User
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Username: {user.username}
            </p>
          </div>
          <StandardButton
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </StandardButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  error={errors.firstName}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  error={errors.lastName}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.lastName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={errors.email}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <StandardInput
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  error={errors.phone}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <StandardInput
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Software Engineer"
                />
              </div>
            </div>
          </div>

          {/* Role and Department */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Role & Department
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Roles (Multi-select) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Roles <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                  {rolesLoading || isLoadingRoles ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {isLoadingRoles ? 'Loading user roles from backend...' : 'Loading roles...'}
                    </p>
                  ) : roles.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No roles available</p>
                  ) : (
                    <div className="space-y-2">
                      {roles.map(role => (
                        <label
                          key={role.id}
                          className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.roleIds.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                            {role.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.roleIds && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.roleIds}
                  </p>
                )}
                {formData.roleIds.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {formData.roleIds.length} role{formData.roleIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                <div className="mt-3">
                  <RolePermissionPreview roles={selectedRoles} title="Effective User Preview" />
                </div>
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <StandardSelect
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  options={[
                    { value: '', label: 'Select a department...' },
                    ...departments.map(dept => ({ value: dept, label: dept }))
                  ]}
                />
              </div>
            </div>
          </div>

          {/* System Settings */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              System Settings
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Locale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Locale
                </label>
                <StandardSelect
                  value={formData.locale}
                  onChange={(e) => handleChange('locale', e.target.value)}
                  options={[
                    { value: 'en_US', label: 'English (US)' },
                    { value: 'en_GB', label: 'English (UK)' },
                    { value: 'es_ES', label: 'Spanish' },
                    { value: 'fr_FR', label: 'French' }
                  ]}
                />
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timezone
                </label>
                <StandardSelect
                  value={formData.timezone}
                  onChange={(e) => handleChange('timezone', e.target.value)}
                  options={[
                    { value: 'America/New_York', label: 'Eastern Time' },
                    { value: 'America/Chicago', label: 'Central Time' },
                    { value: 'America/Denver', label: 'Mountain Time' },
                    { value: 'America/Los_Angeles', label: 'Pacific Time' },
                    { value: 'Europe/London', label: 'London' },
                    { value: 'Europe/Paris', label: 'Paris' }
                  ]}
                />
              </div>

              {/* Email Signature */}
              <StandardTextarea
                label="Email Signature"
                value={formData.emailSignature}
                onChange={(e) => handleChange('emailSignature', e.target.value)}
                rows={3}
                placeholder="Best regards,&#10;Your Name&#10;Your Title"
                className="col-span-2"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardButton
              variant="secondary"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              leftIcon={isSubmitting ? undefined : <Save className="h-4 w-4" />}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </StandardButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
